import { useEffect, useMemo, useState } from "react";

import QUERY_TAGS from "../search/queryTags";
import useQuery from "../search/useQuery";
import {
  checkDeliverability,
  deliveryNotice,
  haversineKm,
  isOnlineStatus,
} from "../detail/deliverability";
import { cleanSupplierName, coordinatesOf } from "../detail/detailMappers";

import {
  isAccessoryProduct,
  productImages,
  specRows,
} from "./accessoryMappers";
import {
  materialInStock,
  materialOriginalPrice,
  materialPrice,
  materialPricesDiffer,
} from "./accessoryMoney";
import { fetchAccessory, fetchSupplierRating } from "./accessoriesService";

const PRODUCT_STALE_TIME = 900000;
const RATING_STALE_TIME = 600000;

const useAccessoryDetail = ({ productId, coordinates }) => {
  const product = useQuery({
    key: productId ? `accessories:product:${productId}` : null,
    tag: QUERY_TAGS.PRODUCT_DETAIL,
    fetcher: () => fetchAccessory(productId),
    staleTime: PRODUCT_STALE_TIME,
  });

  const dto = product.currentData || null;
  const supplierId = dto?.supplier?.id ?? null;

  const rating = useQuery({
    key: supplierId ? `accessories:rating:${supplierId}` : null,
    tag: QUERY_TAGS.SUPPLIER_RATINGS,
    fetcher: () => fetchSupplierRating(supplierId),
    staleTime: RATING_STALE_TIME,
  });

  const materials = useMemo(() => dto?.containerMaterials || [], [dto]);

  const [materialId, setMaterialId] = useState(null);

  useEffect(() => {
    setMaterialId(materials[0]?.id ?? null);
  }, [materials]);

  const supplier = useMemo(() => {
    const raw = dto?.supplier;

    if (!raw) return null;

    const supplierCoordinates = coordinatesOf(raw.address);
    const radiusMeters = Number(raw.deliveryRadiusMeters);

    return {
      id: raw.id ?? null,
      name: cleanSupplierName(raw.name),
      logoUrl: raw.referenceUrlLogo || null,
      online: isOnlineStatus(raw.status),
      status: raw.status || null,
      deliveryRadiusKm: Number.isFinite(radiusMeters) ? radiusMeters / 1000 : null,
      coordinates: supplierCoordinates,
      distanceKm: haversineKm(coordinates, supplierCoordinates),
      addressLine: [raw.address?.address1, raw.address?.city]
        .filter(Boolean)
        .join(", "),
    };
  }, [dto, coordinates]);

  const notice = useMemo(() => {
    if (!supplier) return null;

    const { deliverable, reason, radiusKm, distanceKm } =
      checkDeliverability(supplier);

    if (deliverable) return null;

    return deliveryNotice({
      reason,
      name: supplier.name,
      radiusKm,
      distanceKm,
    });
  }, [supplier]);

  const price = useMemo(() => materialPrice(dto, materialId), [dto, materialId]);

  const originalPrice = useMemo(
    () => materialOriginalPrice(dto, materialId),
    [dto, materialId]
  );

  const inStock = useMemo(
    () => materialInStock(dto, materialId),
    [dto, materialId]
  );

  const materialChips = useMemo(() => {
    if (materials.length < 2) return [];

    const showPrices = materialPricesDiffer(dto);

    return materials.map((material) => ({
      id: material.id,
      label: material.defaultTitle || material.code || "",
      price: showPrices ? materialPrice(dto, material.id) : null,
      inStock: materialInStock(dto, material.id),
    }));
  }, [dto, materials]);

  return {
    product: dto,
    images: useMemo(() => productImages(dto), [dto]),
    isAccessory: isAccessoryProduct(dto),
    specs: useMemo(() => specRows(dto), [dto]),
    supplier,
    rating: rating.currentData || { average: 0, total: 0 },
    notice,
    materialId,
    setMaterialId,
    materialChips,
    price,
    originalPrice,
    discounted:
      Number.isFinite(originalPrice?.value) &&
      Number.isFinite(price?.value) &&
      originalPrice.value > price.value,
    inStock,
    isLoading: !product.isSuccess && !product.isError,
    isError: product.isError,
    refetch: product.refetch,
  };
};

export default useAccessoryDetail;
